// @flow
/* eslint-disable react/no-unused-prop-types, react/no-unused-state */

import * as React from 'react';
import * as wjcCore from 'wijmo/wijmo';
import { withApollo } from 'react-apollo';
import clone from 'clone-deep';

import type {
  ActionStatus,
  SaveStatus,
  UserViewState,
  ColumnsMap,
  SortingDefinition,
  GroupPanelsDefinition,
  GridItem,
} from '../types';
import {
  filterColPropertiesToBeSaved,
  parseData,
  hideColumnsForNonRepUsers,
  isUserARepresentative,
  resetAndPopulateGroupPanels,
  prepareDefaultColumns,
  resetAndPopulateSortDescriptions,
  getCollapsedGroupLevels,
  applyCollapsedGroupLevels,
} from '../utils';
import type { ChangeOrderTypeProps } from '../context/orderContent.context';
import { ColumMap } from "../types";
import { updateItemPriceFields, itemHasPricesForState } from '../utils/priceUtils';

type Props = {
  children: ({
    selectedItems: number,
    currentItem: any,
    gridUpdated: number,
    collection: any,
    columnsMapping: ColumnsMap,
    onRowSelected: any => mixed,
    onGridUpdated: () => mixed,
    onViewSaved: (SaveStatus, UserViewState) => typeof undefined,
    orderActiveStatus: ActionStatus,
    orderRemoveStatus: ActionStatus,
    orderCreateStatus: ActionStatus,
    onOrderActivated: ActionStatus => typeof undefined,
    onOrderRemoved: ActionStatus => typeof undefined,
    onOrderCreated: ActionStatus => typeof undefined,
    onItemAdded: ActionStatus => typeof undefined,
    onViewReset: () => mixed,
    onApplyViewSettings: (viewSettings: Object) => mixed,
    onGridRef: any => mixed,
    onGridWillUnmount: any => mixed,
    onGroupPanelChanged: any => mixed,
    onColSorted: any => mixed,
    onColResized: any => mixed,
    onColDragged: any => mixed,
    onColFreezed: any => mixed,
    onCellEditEnded: any => mixed,
    onColumnsMappingChange: ColumnsMap => mixed,
    onCurrentItemChanged: any => mixed,
    onNotesSaved: any => mixed,
    onItemUpdated: (any, any, any) => mixed,
    onRowFreezed: any => mixed,
    onClearSelection: () => mixed,
    saveStatus: SaveStatus,
    gridRef: any,
    grid: any,
    userViewState: UserViewState,
    refetching: boolean,
    refetch: (?boolean) => mixed,
    hasAllColumnsHidden: boolean,
    frozenColumns: number,
    isLoading: boolean,
    isRefetching: boolean,
    isCollapseLoading: boolean,
    setCollapseLoading: (boolean) => mixed,
    hasDataToDisplay: boolean,
    clearCollectionAndResetChangedOrderItems: () => mixed,
    getGridFilterDefinition: () => ?string,
    updatePriceFields: (string, string, any) => number,
  }) => React.Node,
  client?: any,
  data: any,
  editMutation?: any,
  columnsMapping: ColumnsMap,
  defaultSorting: SortingDefinition,
  defaultGroupPanels: GroupPanelsDefinition,
  loading: boolean,
  sorting: Array<?{
    property: string,
    ascending: boolean,
  }>,
  defaultColumns: any,
  refetching: boolean,
  refetched: boolean,
  refetch: (?boolean) => mixed,
  frozenColumns: number,
  groups: Array<string>,
  filterDefinition?: string,
  state?: string,
  paginated?: boolean,
  active: boolean,
  orderItems?: wjcCore.CollectionView,
  onChangeOrderItems?: ChangeOrderTypeProps,
  hasLocalOrderItems?: boolean,
  forceRefreshAfterUpdate?: boolean,
  collectionFilter?: GridItem => boolean,
  allowRemoval?: boolean,
  allowAddition?: boolean,
  selectedState?: string,
  shouldKeepCollection?: boolean,
  isMobile: Boolean,
  globalFilters?: string,
};

type State = {
  selectedItems: number,
  currentItem: any,
  gridUpdated: number,
  saveStatus: SaveStatus,
  orderCreateStatus: ActionStatus,
  orderActiveStatus: ActionStatus,
  orderRemoveStatus: ActionStatus,
  itemAddedStatus: ActionStatus,
  columnsMapping: ColumnsMap,
  defaultColumnsMapping: ColumnsMap,
  defaultSorting: SortingDefinition,
  collection: any,
  gridRef: any,
  userViewState: UserViewState,
  hasAllColumnsHidden: boolean,
  frozenColumns: number,
  hasDataToDisplay: boolean,
  withCustomMapping: boolean,
  withCustomData: boolean,
  state?: string,
  playResetAnimation: boolean,
  isCollapseLoading: boolean,
  globalFilters?: string,
  selectedState?: string,
};

class GridApiController extends React.Component<Props, State> {
  defaultColumns: Array<{}>;
  firstRun = true;
  errorTimer: TimeoutID;
  orderErrorTimer: TimeoutID;
  orderStatusTimer: TimeoutID;
  orderActiveStatusTimer: TimeoutID;
  orderRemoveStatusTimer: TimeoutID;
  orderActiveErrorTimer: TimeoutID;
  orderRemoveErrorTimer: TimeoutID;
  itemAddedTimer: TimeoutID;
  itemAddedErrorTimer: TimeoutID;
  rowSelectedTimer: TimeoutID;
  unmounted: boolean;
  gridFilter: any;
  pendingFilterDefinition: ?string;
  lastGlobalFilters: ?string;
  lastSelectedState: ?string;
  currentStateFilter: ?string;
  currentMonthFilter: ?string;

  static defaultProps = {
    active: true,
    isMobile: false
  };

  constructor(props: Props) {
    super(props);

    this.gridFilter = null;
    this.currentStateFilter = props.selectedState;
    this.currentMonthFilter = null;
    this.lastGlobalFilters = props.globalFilters;
    this.lastSelectedState = props.selectedState;
    this.pendingFilterDefinition = this.normalizeFilterDefinition(props.filterDefinition);

    // Cache of last known non-empty collapsed levels.
    // Survives zero-row filter states (e.g., Orders "Samples" button removes
    // all rows, so getCollapsedGroupLevels returns [] because there are no
    // GroupRows to read). When switching back to a filter with rows, we
    // restore from this cache instead of losing the collapsed state.
    this._lastKnownCollapsedLevels = [];

    // Pending collapse level to apply after React re-render.
    // Set in componentDidUpdate/updatePriceFields, consumed by setTimeout(0) fallback.
    this._pendingCollapseLevel = null;

    const hasValidColumnsMapping = Array.isArray(props.columnsMapping) && props.columnsMapping.length > 0;
    const initialColumnsMapping = hasValidColumnsMapping ? props.columnsMapping : props.defaultColumns;
    const initialUserViewState = hasValidColumnsMapping ? 'custom' : 'default';
    const initialWithCustomMapping = hasValidColumnsMapping;

    this.state = {
      selectedItems: 0,
      currentItem: null,
      gridUpdated: 0,
      saveStatus: '',
      orderCreateStatus: '',
      orderActiveStatus: '',
      orderRemoveStatus: '',
      itemAddedStatus: '',
      columnsMapping: initialColumnsMapping,
      defaultColumnsMapping: clone(props.defaultColumns),
      defaultSorting: clone(props.defaultSorting),
      collection: undefined,
      gridRef: undefined,
      userViewState: initialUserViewState,
      hasAllColumnsHidden: false,
      frozenColumns: props.frozenColumns || 0,
      hasDataToDisplay: true,
      withCustomMapping: initialWithCustomMapping,
      withCustomData: false,
      playResetAnimation: false,
      multiColumnSort: false,
      isCollapseLoading: false,
      globalFilters: props.globalFilters,
      selectedState: props.selectedState,
    };
  }

  normalizeFilterDefinition = (filterDefinition: any): ?string => {
    if (!filterDefinition) return null;

    let filterString = filterDefinition;
    if (typeof filterDefinition === 'object') {
      if (filterDefinition[0] !== undefined) {
        filterString = Object.values(filterDefinition).join('');
      } else {
        filterString = JSON.stringify(filterDefinition);
      }
    }
    
    if (filterString) {
      try {
        const parsed = JSON.parse(filterString);
        if (!parsed.filters || parsed.filters.length === 0) return null;
      } catch (e) {}
    }
    
    return filterString || null;
  }
  
  getDefaultSortingArray = (): Array<any> => {
    const defaultSorting = this.props.defaultSorting;
    if (!defaultSorting) return [];
    if (Array.isArray(defaultSorting)) return defaultSorting;
    if (defaultSorting.sorting && Array.isArray(defaultSorting.sorting)) return defaultSorting.sorting;
    return [];
  }

  getDefaultGroupsArray = (): Array<any> => {
    const defaultGroupPanels = this.props.defaultGroupPanels;
    if (!defaultGroupPanels) return [];
    if (Array.isArray(defaultGroupPanels)) return defaultGroupPanels;
    if (defaultGroupPanels.groups && Array.isArray(defaultGroupPanels.groups)) return defaultGroupPanels.groups;
    return [];
  }

  /**
   * Get the grid instance from the stored gridRef.
   * GridApiController stores the grid differently than GridFlaskController:
   * gridRef is set via onGridRef and contains a .grid property.
   * @returns {any} The FlexGrid instance, or null
   */
  getGrid = (): any => {
    return this.state.gridRef && this.state.gridRef.grid;
  }

  /**
   * Capture collapsed group levels with fallback to cached levels
   * when the grid has zero GroupRows (e.g., filter removed all data).
   * 
   * Three cases:
   * 1. Some rows collapsed -> cache and return levels
   * 2. Zero GroupRows (empty filter result) -> return cached levels  
   * 3. GroupRows exist but all expanded -> cache empty and return empty
   * 
   * This solves the Orders "Samples" button problem: clicking Samples removes
   * all rows, so getCollapsedGroupLevels returns []. Without the cache,
   * switching back to "All Orders" would lose the previously collapsed state.
   */
  _captureCollapsedLevels = (): Array<number> => {
    const grid = this.getGrid();
    if (!grid) return this._lastKnownCollapsedLevels || [];

    const levels = getCollapsedGroupLevels(grid);

    if (levels.length > 0) {
      // Got real collapsed levels - cache them
      this._lastKnownCollapsedLevels = levels;
      return levels;
    }

    // Empty result - determine why: zero rows or genuinely expanded?
    let hasGroupRows = false;
    if (grid.rows) {
      for (let i = 0; i < grid.rows.length; i++) {
        if (grid.rows[i].hasChildren) {
          hasGroupRows = true;
          break;
        }
      }
    }

    if (!hasGroupRows) {
      // Zero GroupRows (filter removed all data) - fall back to cache
      return this._lastKnownCollapsedLevels || [];
    }

    // Grid has GroupRows but none are collapsed - genuinely expanded
    this._lastKnownCollapsedLevels = [];
    return [];
  }

  /**
   * Apply the desired collapse level to the grid.
   * Used as both synchronous post-apply and async fallback.
   * 
   * @param {number|null} targetLevel - The collapse level to apply, or null to skip
   * @param {string} context - Logging context label
   */
  _applyCollapseLevel = (targetLevel: ?number, context: string) => {
    if (targetLevel === null || targetLevel === undefined) return;
    const grid = this.getGrid();
    if (!grid) {
      console.log(`[${context}] No grid available, skipping collapse`);
      return;
    }
    try {
      grid.collapseGroupsToLevel(targetLevel);
      // Verify it worked by checking a few rows
      let collapsedCount = 0;
      let expandedCount = 0;
      if (grid.rows) {
        for (let i = 0; i < Math.min(grid.rows.length, 50); i++) {
          if (grid.rows[i].hasChildren) {
            if (grid.rows[i].isCollapsed) collapsedCount++;
            else expandedCount++;
          }
        }
      }
      console.log(`[${context}] Applied collapseGroupsToLevel(${targetLevel}), verified: ${collapsedCount} collapsed, ${expandedCount} expanded`);
    } catch (e) {
      console.warn(`[${context}] Error:`, e);
    }
  }

  /**
   * Expand all groups in the grid using Wijmo's optimized batch method.
   */
  expandAllGroups = (grid: any) => {
    if (!grid) return;
    try {
      grid.collapseGroupsToLevel(100);
    } catch (e) {
      console.warn('[expandAllGroups] collapseGroupsToLevel failed, using fallback:', e);
      if (grid.rows) grid.rows.forEach(row => { if (row.hasChildren) row.isCollapsed = false; });
    }
  }

  componentDidMount() {
    this.oldDerivedStateFromProps(this.props, this.state)
  }

  oldDerivedStateFromProps(nextProps: Props, prevState: State) {
    if (prevState.collection) {
      const currentItem = prevState.collection.currentItem;
      const collection = prevState.collection;

      if (!nextProps.shouldKeepCollection) {
        const dataSource = Array.isArray(nextProps.data) ? nextProps.data : [];
        const data = parseData(dataSource);

        collection.beginUpdate();
        try {
          collection.sourceCollection = data;
          collection.currentItem = currentItem;
        } finally {
          collection.endUpdate();
        }
      }

      if (
        nextProps.hasLocalOrderItems &&
        nextProps.orderItems &&
        nextProps.orderItems.totalItemCount &&
        nextProps.globalFilters === prevState.globalFilters &&
        nextProps.selectedState === prevState.selectedState
      ) {
        nextProps.orderItems.items.forEach((i: GridItem) => {
          if (collection.items.some(item => item.Code === i.Code)) {
            const currentItem = collection.items.find(item => item.Code === i.Code);

            if (i.Cases === 0 && i.Bottles === 0 && currentItem && currentItem.maxItemQty.ActionPerOrderType !== 'remove' && nextProps.allowRemoval) {
              collection.remove(currentItem);
            } else if (currentItem) {
              collection.editItem(currentItem);
              if (currentItem.maxItemQty !== undefined) {
                const { ActionPerOrderType, ...rest } = i.maxItemQty;
                currentItem.maxItemQty = { ...currentItem.maxItemQty, ...rest };
              }
              if (currentItem.Cases !== undefined && currentItem.Cases !== i.Cases) currentItem.Cases = i.Cases;
              if (currentItem.Bottles !== undefined && currentItem.Bottles !== i.Bottles) currentItem.Bottles = i.Bottles;
              if (currentItem.Qty !== undefined && currentItem.Qty !== i.Qty) currentItem.Qty = i.Qty;
              if (currentItem.Price !== undefined && JSON.stringify(currentItem.Price) !== JSON.stringify(i.Price)) currentItem.Price = i.Price;
              collection.commitEdit();
            }
          } else if ((i.Cases > 0 || i.Bottles > 0) && nextProps.allowAddition) {
            let newItem = collection.addNew();
            Object.keys(i).forEach(key => { newItem[key] = i[key]; });
            collection.commitNew();
          }
        });

        if (nextProps.allowRemoval && collection.totalItemCount === 0) {
          return this.setState({ hasDataToDisplay: false });
        }

        if (!nextProps.allowRemoval || !nextProps.allowAddition || (Array.isArray(nextProps.data) && collection.totalItemCount === nextProps.data.length)) {
          return null;
        }
      }
    }

    if (nextProps.loading === false && nextProps.data) {
      const dataSource = Array.isArray(nextProps.data) ? nextProps.data : [];

      // FIX: Removed mobile guard that blocked collection creation when saved views exist.
      // Original line: if (this.props.isMobile && prevState.withCustomMapping) return;
      // This caused grids to never render on mobile when any saved view was active,
      // because the entire data processing pipeline was short-circuited before
      // the collection could be created. Desktop was unaffected since the guard
      // only fired when isMobile=true.

      let collection;
      const data = parseData(dataSource);

      if (!prevState.collection) {
        collection = new wjcCore.CollectionView(data);
      } else {
        const currentItem = prevState.collection.currentItem;
        collection = prevState.collection;
        collection.beginUpdate();
        try {
          collection.sourceCollection = data;
          collection.currentItem = currentItem;
        } finally {
          collection.endUpdate();
        }
      }

      if (nextProps.paginated) collection.pageSize = 50;
      if (nextProps.collectionFilter) collection.filter = nextProps.collectionFilter;

      let newState: { collection: any, frozenColumns?: number, hasDataToDisplay: boolean } = {
        collection,
        hasDataToDisplay: collection.totalItemCount > 0,
        globalFilters: nextProps.globalFilters,
        selectedState: nextProps.selectedState,
      };

      if (nextProps.refetched && prevState.hasDataToDisplay) {
        return this.setState({...newState});
      }

      if (nextProps.frozenColumns != null) newState.frozenColumns = nextProps.frozenColumns;

      const hasUserCustomMapping = Array.isArray(nextProps.columnsMapping) && nextProps.columnsMapping.length > 0;
      const defaultSortingArray = this.getDefaultSortingArray();
      const defaultGroupsArray = this.getDefaultGroupsArray();

      const hasExplicitSorting = Array.isArray(nextProps.sorting) && nextProps.sorting.length > 0;
      const hasDefaultSorting = defaultSortingArray.length > 0;
      const needsSorting = hasExplicitSorting || hasDefaultSorting;
      
      const hasExplicitGroups = Array.isArray(nextProps.groups) && nextProps.groups.length > 0;
      const hasDefaultGroups = defaultGroupsArray.length > 0;
      const needsGrouping = hasExplicitGroups || (hasDefaultGroups && !hasUserCustomMapping);

      if (needsSorting || needsGrouping) {
        collection.beginUpdate();
        try {
          if (hasExplicitSorting) {
            resetAndPopulateSortDescriptions(nextProps.sorting, collection);
          } else if (hasDefaultSorting) {
            resetAndPopulateSortDescriptions(defaultSortingArray, collection);
          }
          if (hasExplicitGroups) {
            resetAndPopulateGroupPanels(nextProps.groups, collection);
          } else if (hasDefaultGroups && !hasUserCustomMapping) {
            resetAndPopulateGroupPanels(defaultGroupsArray, collection);
          }
        } finally {
          collection.endUpdate();
        }
      }

      const isUserARep = isUserARepresentative(data);
      const defaultColumns = isUserARep ? nextProps.defaultColumns : hideColumnsForNonRepUsers(nextProps.defaultColumns);

      let defaultColumnsMappingState;
      if (nextProps.defaultColumns) {
        defaultColumnsMappingState = { defaultColumnsMapping: prepareDefaultColumns(defaultColumns, collection.groupDescriptions) };
      } else {
        defaultColumnsMappingState = {};
      }

      newState = { ...newState, ...defaultColumnsMappingState, defaultSorting: nextProps.defaultSorting };

      if (hasUserCustomMapping && !prevState.withCustomMapping) {
        return this.setState({
          withCustomMapping: true,
          ...newState,
          userViewState: 'custom',
          columnsMapping: isUserARep ? nextProps.columnsMapping : hideColumnsForNonRepUsers(nextProps.columnsMapping),
        });
      }

      const defCustomMapping = !prevState.withCustomMapping
        ? { columnsMapping: filterColPropertiesToBeSaved(defaultColumns, ['currentSort']) }
        : {};

      return this.setState({ ...newState, userViewState: 'default', ...defCustomMapping });
    } else if (prevState.hasDataToDisplay === true && nextProps.data === undefined) {
      return this.setState({ hasDataToDisplay: false });
    }

    return null;
  }

  componentWillUnmount() {
    this.unmounted = true;
    [this.errorTimer, this.orderErrorTimer, this.orderStatusTimer, this.orderActiveStatusTimer, 
     this.orderRemoveStatusTimer, this.orderActiveErrorTimer, this.orderRemoveErrorTimer,
     this.itemAddedTimer, this.itemAddedErrorTimer, this.rowSelectedTimer].forEach(t => t && clearTimeout(t));
  }

  onGridWillUnmount = () => {
    this.state.collection.groupDescriptions.clear();
    this.state.collection.sortDescriptions.clear();
    this.setState({ collection: undefined });
  };

  onClearSelection = () => { this.setState({ selectedItems: 0 }); };

  onRowSelected = (gridInstance: any) => {
    const { selectedItems } = gridInstance;
    if (selectedItems.length !== 0) {
      if (this.rowSelectedTimer) clearTimeout(this.rowSelectedTimer);
      this.rowSelectedTimer = setTimeout(() => {
        if (this.unmounted || !this.props.active) return;
        this.setState(state => ({ selectedItems: selectedItems.length, currentItem: state.collection && state.collection.currentItem }));
      }, 25);
    }
  };

  onViewSaved = (status: SaveStatus, viewState: UserViewState) => {
    this.setState({ saveStatus: status || 'saved', userViewState: viewState === 'def_custom' ? 'custom' : viewState });
    if (status !== 'error') {
      this.errorTimer = setTimeout(() => { this.setState({ saveStatus: '' }); }, 1000);
    }
  };

  onOrderCreated = (status: ActionStatus) => {
    this.orderStatusTimer = setTimeout(() => { this.setState({ orderCreateStatus: status }); }, 2000);
    if (status !== 'error') {
      this.orderErrorTimer = setTimeout(() => { this.setState({ orderCreateStatus: '' }); }, 3000);
    }
  };

  onOrderActivated = (status: ActionStatus) => {
    this.setState({ orderActiveStatus: status });
    if (status !== 'error') {
      this.orderActiveErrorTimer = setTimeout(() => { this.setState({ orderActiveStatus: '' }); }, 1000);
    }
  };

  clearCollectionAndResetChangedOrderItems = () => {
    if (this.props.allowRemoval && this.state.collection && Array.isArray(this.state.collection.items)) {
      this.state.collection.items.forEach(item => { this.state.collection.remove(item); });
    }
    if (typeof this.props.onChangeOrderItems === 'function') {
      this.props.onChangeOrderItems({ action: 'reset' });
    }
  };

  onOrderRemoved = (status: ActionStatus) => {
    this.setState({ orderRemoveStatus: status });
    if (status !== 'error') {
      this.clearCollectionAndResetChangedOrderItems();
      this.orderRemoveErrorTimer = setTimeout(() => { this.setState({ orderRemoveStatus: '' }); }, 1000);
    }
  };

  onItemAdded = (status: ActionStatus) => {
    this.itemAddedTimer = setTimeout(() => { this.setState({ itemAddedStatus: status, withCustomData: true }); }, 2000);
    if (status !== 'error') {
      this.itemAddedErrorTimer = setTimeout(() => { this.setState({ itemAddedStatus: '' }); }, 3000);
    }
  };

  resetSorting = () => { resetAndPopulateSortDescriptions(this.getDefaultSortingArray(), this.state.collection); };
  resetGrouping = () => { resetAndPopulateGroupPanels(this.getDefaultGroupsArray(), this.state.collection); };

  toggleMultiSort = () => {
    this.setState(prevState => {
      if (prevState.multiColumnSort) this.state.collection.sortDescriptions.clear();
      return { ...prevState, multiColumnSort: !prevState.multiColumnSort };
    });
  }

  setCollapseLoading = (isLoading: boolean) => { this.setState({ isCollapseLoading: isLoading }); }

  hideLoadingWhenReady = () => {
    requestAnimationFrame(() => { requestAnimationFrame(() => { this.setCollapseLoading(false); }); });
  }

  /**
   * Check if there's an active grid filter (column filters from FlexGridFilter).
   * Returns true if gridFilter has a non-empty filterDefinition.
   */
  hasActiveGridFilter = (): boolean => {
    if (!this.gridFilter) return false;
    const filterDef = this.gridFilter.filterDefinition;
    return !!this.normalizeFilterDefinition(filterDef);
  }

  /**
   * Create a combined filter function that includes both:
   * 1. Our visibility filter (items must have effective prices for current state/month)
   * 2. The grid's column filters (if any are active)
   * 
   * This should be called whenever we need to set collection.filter to ensure
   * we don't lose either filter type.
   * 
   * @param {string} filterState - The state code for visibility filtering
   * @param {string} filterMonth - The month for visibility filtering
   * @returns {Function} Combined filter function
   */
  createCombinedFilter = (filterState: string, filterMonth: string): Function => {
    // Check if there's an active grid filter we need to preserve
    const hasGridFilter = this.hasActiveGridFilter();
    
    // If there's an active grid filter, we need to trigger FlexGridFilter to reapply
    // its filter, then combine with ours. We do this by reading collection.filter
    // AFTER FlexGridFilter has set it.
    if (hasGridFilter && this.gridFilter) {
      // Force FlexGridFilter to reapply its filter
      const currentFilterDef = this.gridFilter.filterDefinition;
      this.gridFilter.filterDefinition = currentFilterDef;
    }
    
    // Capture any existing filter (may be from FlexGridFilter)
    const { collection } = this.state;
    const existingFilter = collection ? collection.filter : null;
    
    console.log(`[createCombinedFilter] Creating filter: state=${filterState}, month=${filterMonth}, hasGridFilter=${hasGridFilter}`);
    
    // Return combined filter
    return (item) => {
      // First check our visibility filter (items must have effective prices)
      if (item.linkedPrices) {
        if (!itemHasPricesForState(item, filterState, filterMonth)) {
          return false;
        }
      }
      
      // Then check the existing filter (grid column filters) if one exists
      // But avoid infinite recursion by checking if it's not our own combined filter
      if (existingFilter && hasGridFilter) {
        return existingFilter(item);
      }
      
      return true;
    };
  }

  /**
   * Reapply the price visibility filter on the collection.
   * This is used when no grid filter is being applied (e.g., view reset, clear filters).
   * For cases where grid filter IS being applied, use applyGridFilterAndReapplyVisibility instead.
   */
  reapplyPriceVisibilityFilter = () => {
    const { collection } = this.state;
    if (!collection) return;
    
    // Get filter values - use stored values or extract from props
    let filterState = this.currentStateFilter;
    let filterMonth = this.currentMonthFilter;
    
    // Fallback to props if stored values aren't set
    if (!filterState) filterState = this.props.selectedState;
    if (!filterMonth && this.props.globalFilters) {
      // globalFilters format: "${state}${month}" e.g., "J2026-01"
      filterMonth = this.props.globalFilters.slice(1);
    }
    
    if (!filterState || !filterMonth) {
      console.warn('[reapplyPriceVisibilityFilter] No filter values available, skipping');
      return;
    }
    
    console.log(`[reapplyPriceVisibilityFilter] Applying visibility-only filter: state=${filterState}, month=${filterMonth}`);
    
    // Set just the visibility filter (no grid column filters)
    collection.filter = (item) => {
      if (item.linkedPrices) return itemHasPricesForState(item, filterState, filterMonth);
      return true;
    };
    
    collection.refresh();
  }

  onViewReset = () => {
    console.time('[ViewReset] Total');
    this.setCollapseLoading(true);
    
    requestAnimationFrame(() => {
      setTimeout(() => {
        try {
          const collection = this.state.collection;
          
          if (collection) {
            collection.beginUpdate();
            try {
              this.resetSorting();
              this.resetGrouping();
            } finally {
              collection.endUpdate();
            }
          } else {
            this.resetSorting();
            this.resetGrouping();
          }

          // Clear grid filter and apply visibility-only filter
          this.clearGridFilter();
          this.reapplyPriceVisibilityFilter();

          const { freezeBarCol } = this.state.gridRef || {};
          if (freezeBarCol && typeof freezeBarCol.resetFreezeBar === 'function') freezeBarCol.resetFreezeBar();

          const grid = this.getGrid();
          if (grid) this.expandAllGroups(grid);

          // Reset collapsed levels cache since we're expanding all
          this._lastKnownCollapsedLevels = [];
          this._pendingCollapseLevel = null;

          this.setState(state => ({
            saveStatus: (state.userViewState === 'default' || state.userViewState === 'def_custom') && state.saveStatus !== 'unsaved' ? '' : 'reset',
            userViewState: 'default',
            columnsMapping: clone(this.props.defaultColumns),
            frozenColumns: 0,
          }));
          
          console.timeEnd('[ViewReset] Total');
        } catch (e) {
          console.error('[onViewReset] Error resetting view:', e);
          console.timeEnd('[ViewReset] Total');
        } finally {
          this.hideLoadingWhenReady();
        }
      }, 50);
    });
  };

  onApplyViewSettings = (viewSettings: Object) => {
    console.time('[ViewSwitch] Total');
    this.setCollapseLoading(true);
    
    requestAnimationFrame(() => {
      setTimeout(() => {
        try {
          const groups = viewSettings.groups || [];
          const sorting = viewSettings.sorting || [];
          const filterDefinition = viewSettings.filterDefinition || '';
          const columns = viewSettings.columns || [];
          const frozenColumns = viewSettings.frozenColumns || 0;
          
          const collection = this.state.collection;
          
          if (collection) {
            collection.beginUpdate();
            try {
              resetAndPopulateGroupPanels(groups, collection);
              resetAndPopulateSortDescriptions(sorting, collection);
            } finally {
              collection.endUpdate();
            }
          }

          // Apply grid filter AND combine with visibility filter
          this.applyGridFilterAndReapplyVisibility(filterDefinition);

          const grid = this.getGrid();
          if (grid) this.expandAllGroups(grid);

          // Reset collapsed levels cache since we're expanding all
          this._lastKnownCollapsedLevels = [];
          this._pendingCollapseLevel = null;

          this.setState({
            saveStatus: '',
            userViewState: 'custom',
            columnsMapping: columns.length > 0 ? clone(columns) : clone(this.props.defaultColumns),
            frozenColumns: frozenColumns,
          });
          
          console.timeEnd('[ViewSwitch] Total');
        } catch (e) {
          console.error('[onApplyViewSettings] Error applying view settings:', e);
          console.timeEnd('[ViewSwitch] Total');
        } finally {
          this.hideLoadingWhenReady();
        }
      }, 50);
    });
  };

  getCommonStateOnGridChanged = (state: State, columnsMapping?: ColumnsMap) => {
    const columns = state.gridRef.grid.columns;
    const tempColMapping: ColumnsMap = [];
    columns.forEach(item => {
      tempColMapping.push({ binding: item.binding, header: item.header, visible: item.isVisible, size: item.size });
    });

    const newColMapping = tempColMapping.map(newCol => {
      const headerMatch = state.columnsMapping.find(oldCol => oldCol.binding === newCol.binding);
      return { ...headerMatch, ...newCol };
    });

    return {
      withCustomMapping: true,
      saveStatus: 'unsaved',
      columnsMapping: columnsMapping || newColMapping,
      userViewState: state.userViewState === 'default' || state.userViewState.indexOf('def_') !== -1 ? 'def_custom' : 'custom',
    };
  };

  onColSorted = () => { this.setState(state => this.getCommonStateOnGridChanged(state)); };
  onColResized = () => { this.setState(state => this.getCommonStateOnGridChanged(state)); };
  onColDragged = () => { this.setState(state => this.getCommonStateOnGridChanged(state)); };
  onGroupPanelChanged = () => { this.setState(state => this.getCommonStateOnGridChanged(state)); };

  onColumnsMappingChange = (changedColumns: ColumnsMap) => {
    this.setState(state => {
      const columnsMapping = state.columnsMapping.map(col => {
        const changedCol = changedColumns.find(i => i.binding === col.binding);
        return { ...col, ...changedCol };
      });
      return {
        ...this.getCommonStateOnGridChanged(state, columnsMapping),
        hasAllColumnsHidden: !columnsMapping.some(col => col.visible === true),
      };
    });
  };

  onCellEditEnded = async args => {
    if (!this.props.client || typeof this.props.client.mutate !== 'function') return;
    if (!this.props.editMutation) {
      console.log('You have to specify a `editMutation` mutation in order to do something with the changes', args);
      return;
    }
    try {} catch (e) { console.log('something went wrong with onCellEditing', e); }
  };

  onColFreezed = (colNumber: number) => { this.setState(state => ({ ...this.getCommonStateOnGridChanged(state), frozenColumns: colNumber })); };
  onGridUpdated = () => { this.setState(state => ({ gridUpdated: state.gridUpdated + 1 })); };
  onGridRef = (g: any) => { this.setState({ gridRef: g }); };
  onCurrentItemChanged = (currentItem: any) => { this.setState({ currentItem }); };

  onNotesSaved = async (changes: { fieldName: string, value: string }) =>
    new Promise(r => {
      this.setState(state => {
        const { grid } = state.gridRef;
        const item = state.collection.currentItem;
        grid.collectionView.editItem(item);
        item[changes.fieldName] = changes.value;
        grid.collectionView.commitEdit();
        return { currentItem: item, collection: state.collection };
      }, r);
    });

  onItemUpdated = async (changes: Array<{ fieldName: string, updater?: [(item: GridItem) => mixed] | ((item: GridItem) => mixed), value: string }>, Code: string, currentItem: GridItem) =>
    new Promise(r => {
      if (this.state.collection) {
        if (this.props.forceRefreshAfterUpdate && this.state.collection && typeof this.state.collection.refresh === 'function') {
          this.state.collection.refresh();
        }
        r();
      }
    });

  handleResetAnimation = () => {
    this.setState(prevState => ({ ...prevState, playResetAnimation: true }));
    setTimeout(() => { this.setState(prevState => ({ ...prevState, playResetAnimation: false })); }, 2000);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.state.gridRef && this.state.gridRef.grid) {
      if (this.firstRun) this.firstRun = false;
    }
    if (this.props !== prevProps) {
      const dataUnchanged = this.props.data === prevProps.data && this.props.loading === prevProps.loading && this.props.refetching === prevProps.refetching && this.props.refetched === prevProps.refetched;
      
      if (dataUnchanged) {
        this.lastGlobalFilters = this.props.globalFilters;
        this.lastSelectedState = this.props.selectedState;
        return;
      }
      
      // Capture collapsed state BEFORE data change rebuilds GroupRow objects.
      // Uses _captureCollapsedLevels with cache to survive zero-row filter
      // states (e.g., Samples button removes all rows).
      const collapsedLevels = this._captureCollapsedLevels();
      const targetLevel = (collapsedLevels && collapsedLevels.length > 0)
        ? Math.min(...collapsedLevels) : null;
      console.log('[componentDidUpdate] Captured collapsedLevels:', collapsedLevels,
        'targetLevel:', targetLevel,
        'loading:', prevProps.loading, '->', this.props.loading,
        'refetching:', prevProps.refetching, '->', this.props.refetching,
        'dataChanged:', this.props.data !== prevProps.data);
      
      // Store desired collapse level as instance variable so it survives
      // across React re-renders triggered by setState inside oldDerivedStateFromProps.
      this._pendingCollapseLevel = targetLevel;
      
      // CRITICAL: Pre-set Wijmo's internal _collapseLevel BEFORE
      // oldDerivedStateFromProps runs. That method calls collection.endUpdate()
      // which triggers Wijmo to synchronously rebuild GroupRow objects.
      // Each new GroupRow's isCollapsed is set based on the grid's _collapseLevel
      // at creation time. By pre-setting it, new rows are born in the correct
      // collapsed state - no intermediate expanded flash, no race condition.
      // This mirrors the fix in GridFlaskController.onFiltersChanged.
      {
        const grid = this.getGrid();
        if (grid) {
          if (targetLevel !== null) {
            grid.collapseGroupsToLevel(targetLevel);
            console.log('[componentDidUpdate] Pre-set _collapseLevel to', targetLevel);
          } else {
            grid.collapseGroupsToLevel(100);
            console.log('[componentDidUpdate] Pre-set _collapseLevel to 100 (all expanded)');
          }
        }
      }
      
      this.lastGlobalFilters = this.props.globalFilters;
      this.lastSelectedState = this.props.selectedState;
      this.oldDerivedStateFromProps(this.props, this.state);
      
      // LAYER 1 - Synchronous post-apply: Apply collapse AFTER
      // oldDerivedStateFromProps has finished all beginUpdate/endUpdate
      // cycles. GroupRows now exist. Still same JS frame — no paint yet.
      this._applyCollapseLevel(targetLevel, 'cDU-sync');
      
      // LAYER 2 - Async fallback: Also apply after React re-render completes.
      // setState inside oldDerivedStateFromProps triggers a batched re-render
      // which may cause the Wijmo grid component to refresh and reset collapse.
      // setTimeout(0) fires after the microtask queue and re-render.
      if (targetLevel !== null) {
        setTimeout(() => {
          if (this._pendingCollapseLevel === null) return; // cleared by user action
          console.log('[componentDidUpdate] setTimeout(0) fallback firing, level:', this._pendingCollapseLevel);
          this._applyCollapseLevel(this._pendingCollapseLevel, 'cDU-setTimeout0');
          this._pendingCollapseLevel = null;
        }, 0);
        
        // LAYER 3 - RAF fallback: In case setTimeout(0) fires before grid
        // refresh completes (e.g., if grid uses rAF internally).
        requestAnimationFrame(() => {
          if (this._pendingCollapseLevel === null) return;
          console.log('[componentDidUpdate] rAF fallback firing, level:', this._pendingCollapseLevel);
          this._applyCollapseLevel(this._pendingCollapseLevel, 'cDU-rAF');
          this._pendingCollapseLevel = null;
        });
      }
      
      this.onClearSelection();
    }
  }

  setFlexGridFilter = (filterInstance: any) => {
    this.gridFilter = filterInstance;
    if (this.pendingFilterDefinition && this.gridFilter) {
      try { this.gridFilter.filterDefinition = this.pendingFilterDefinition; } catch (e) { console.error('[setFlexGridFilter] Error applying pending filter:', e); }
      this.pendingFilterDefinition = null;
    }
  }

  getGridFilterDefinition = (): ?string => {
    if (this.gridFilter && this.gridFilter.filterDefinition) return this.gridFilter.filterDefinition;
    return null;
  }

  clearGridFilter = () => {
    this.pendingFilterDefinition = null;
    if (this.gridFilter) {
      try { this.gridFilter.filterDefinition = null; } catch (e) { console.error('[clearGridFilter] Error clearing filter:', e); }
    }
  }

  applyGridFilter = (filterDefinition: any) => {
    if (!filterDefinition) { this.clearGridFilter(); return; }
    const filterString = this.normalizeFilterDefinition(filterDefinition);
    if (!filterString) { this.clearGridFilter(); return; }
    
    this.pendingFilterDefinition = filterString;
    setTimeout(() => {
      if (this.gridFilter) {
        try {
          if (filterString) this.gridFilter.filterDefinition = filterString;
          this.pendingFilterDefinition = null;
        } catch (e) { console.error('[applyGridFilter] Error applying filter:', e); }
      }
    }, 0);
  }

  /**
   * Apply grid filter (column filters) AND combine with our visibility filter.
   * 
   * This is needed because FlexGridFilter internally sets collection.filter when
   * filterDefinition is set. We need to COMBINE both filters - the grid's column
   * filters AND our visibility filter that hides items without effective prices.
   * 
   * Approach:
   * 1. Apply grid filter (which sets collection.filter)
   * 2. Capture the grid filter's filter function
   * 3. Create a combined filter that checks BOTH visibility AND grid filter
   * 
   * @param {any} filterDefinition - The filter definition to apply (or null to clear)
   */
  applyGridFilterAndReapplyVisibility = (filterDefinition: any) => {
    const { collection } = this.state;
    
    // Get filter values for visibility check
    let filterState = this.currentStateFilter;
    let filterMonth = this.currentMonthFilter;
    
    // Fallback to props if stored values aren't set
    if (!filterState) filterState = this.props.selectedState;
    if (!filterMonth && this.props.globalFilters) {
      filterMonth = this.props.globalFilters.slice(1);
    }
    
    const hasFilterDefinition = filterDefinition && this.normalizeFilterDefinition(filterDefinition);
    
    // If no grid filter, just apply visibility filter directly
    if (!hasFilterDefinition) {
      this.clearGridFilter();
      
      // Apply visibility-only filter
      if (collection && filterState && filterMonth) {
        console.log(`[applyGridFilterAndReapplyVisibility] No grid filter, applying visibility-only: state=${filterState}, month=${filterMonth}`);
        collection.filter = (item) => {
          if (item.linkedPrices) return itemHasPricesForState(item, filterState, filterMonth);
          return true;
        };
        collection.refresh();
      }
      return;
    }
    
    // Apply grid filter first
    const filterString = this.normalizeFilterDefinition(filterDefinition);
    this.pendingFilterDefinition = filterString;
    setTimeout(() => {
      if (this.gridFilter) {
        try {
          this.gridFilter.filterDefinition = filterString;
          this.pendingFilterDefinition = null;
        } catch (e) { console.error('[applyGridFilterAndReapplyVisibility] Error applying filter:', e); }
      }
    }, 0);
    
    // Then, create a COMBINED filter that includes both grid filter AND visibility filter
    // Use a longer timeout to ensure we run after the grid filter's setTimeout(0)
    setTimeout(() => {
      if (!collection) return;
      
      // Capture the grid filter's filter function (set by FlexGridFilter)
      const gridFilterFn = collection.filter;
      
      console.log(`[applyGridFilterAndReapplyVisibility] Creating combined filter: state=${filterState}, month=${filterMonth}, hasGridFilter=${!!gridFilterFn}`);
      
      // Create combined filter that checks BOTH conditions
      collection.filter = (item) => {
        // First check our visibility filter (items must have effective prices)
        if (item.linkedPrices) {
          if (!itemHasPricesForState(item, filterState, filterMonth)) {
            return false;
          }
        }
        
        // Then check the grid filter (column filters) if one was set
        if (gridFilterFn) {
          return gridFilterFn(item);
        }
        
        return true;
      };
      
      collection.refresh();
    }, 50);
  }

  /**
   * Update price fields for all items in the collection based on state/month.
   * 
   * IMPORTANT: This method must preserve any active grid column filters when
   * setting the visibility filter. If grid filters are active, we create a
   * combined filter that checks both visibility AND grid column conditions.
   * 
   * Also preserves collapsed group state across filter/refresh operations
   * that rebuild GroupRow objects.
   */
  updatePriceFields = (month: string, state: string, forceFilter: any): number => {
    const { collection } = this.state;
    if (!collection) { console.warn('[updatePriceFields] No collection available'); return 0; }
    
    console.time('[PriceUpdate] Total');
    let updatedCount = 0;
    
    const stateChanged = this.currentStateFilter !== state;
    const monthChanged = this.currentMonthFilter !== month;
    this.currentStateFilter = state;
    this.currentMonthFilter = month;
    
    // Capture collapsed state before filter changes rebuild GroupRow objects
    const collapsedLevels = this._captureCollapsedLevels();
    const targetLevel = (collapsedLevels && collapsedLevels.length > 0)
      ? Math.min(...collapsedLevels) : null;
    
    try {
      const items = collection.sourceCollection || collection.items;
      
      items.forEach(item => { if (updateItemPriceFields(item, state, month)) updatedCount++; });
      
      console.log(`[updatePriceFields] Updated ${updatedCount} items for state=${state}, month=${month}`);
      
      if (stateChanged || monthChanged || forceFilter) {
        const filterState = state;
        const filterMonth = month;
        
        // Check if there's an active grid filter we need to preserve
        const hasGridFilter = this.hasActiveGridFilter();
        
        if (hasGridFilter && this.gridFilter) {
          // There's an active grid filter - we need to combine with it
          // First, force FlexGridFilter to reapply its filter to collection.filter
          const currentFilterDef = this.gridFilter.filterDefinition;
          this.gridFilter.filterDefinition = currentFilterDef;
          
          // Capture the grid filter function
          const gridFilterFn = collection.filter;
          
          console.log(`[updatePriceFields] Combining visibility filter with grid filter`);
          
          // Create combined filter
          collection.filter = (item) => {
            // First check our visibility filter
            if (item.linkedPrices) {
              if (!itemHasPricesForState(item, filterState, filterMonth)) {
                return false;
              }
            }
            
            // Then check the grid filter
            if (gridFilterFn) {
              return gridFilterFn(item);
            }
            
            return true;
          };
        } else {
          // No grid filter - just apply visibility filter
          console.log(`[updatePriceFields] Applying visibility-only filter`);
          collection.filter = (item) => {
            if (item.linkedPrices) return itemHasPricesForState(item, filterState, filterMonth);
            return true;
          };
        }
      }
      
      collection.refresh();
    } catch (e) { console.error('[updatePriceFields] Error updating price fields:', e); }
    
    // Post-apply collapse after collection.refresh() rebuilt GroupRows
    this._applyCollapseLevel(targetLevel, 'updatePriceFields-sync');
    
    // Async fallback for any deferred grid refresh
    if (targetLevel !== null) {
      this._pendingCollapseLevel = targetLevel;
      setTimeout(() => {
        if (this._pendingCollapseLevel === null) return;
        this._applyCollapseLevel(this._pendingCollapseLevel, 'updatePriceFields-setTimeout0');
        this._pendingCollapseLevel = null;
      }, 0);
    }
    
    this.onClearSelection();
    
    console.timeEnd('[PriceUpdate] Total');
    return updatedCount;
  }

  /**
   * Clear column filters only (not the state/month visibility filter).
   * After clearing grid filters, reapply the visibility filter to ensure
   * items without effective prices remain hidden.
   */
  clearInternalFilters = () => {
    this.handleResetAnimation();
    // Clear the grid column filters
    setTimeout(() => { if (this.gridFilter) this.gridFilter.filterDefinition = null; }, 0);
    // Reapply visibility filter after grid filter is cleared
    // (FlexGridFilter clears collection.filter when filterDefinition is set to null)
    setTimeout(() => { this.reapplyPriceVisibilityFilter(); }, 50);
  }

  render() {
    return this.props.children({
      selectedItems: this.state.selectedItems,
      currentItem: this.state.currentItem,
      gridUpdated: this.state.gridUpdated,
      collection: this.state.collection,
      columnsMapping: this.state.columnsMapping,
      saveStatus: this.state.saveStatus,
      orderCreateStatus: this.state.orderCreateStatus,
      orderActiveStatus: this.state.orderActiveStatus,
      orderRemoveStatus: this.state.orderRemoveStatus,
      itemAddedStatus: this.state.itemAddedStatus,
      onRowSelected: this.onRowSelected,
      onGridUpdated: this.onGridUpdated,
      onViewSaved: this.onViewSaved,
      onOrderCreated: this.onOrderCreated,
      onOrderActivated: this.onOrderActivated,
      onOrderRemoved: this.onOrderRemoved,
      onItemAdded: this.onItemAdded,
      onViewReset: this.onViewReset,
      onApplyViewSettings: this.onApplyViewSettings,
      onGridRef: this.onGridRef,
      onGridWillUnmount: this.onGridWillUnmount,
      onColSorted: this.onColSorted,
      onColResized: this.onColResized,
      onColDragged: this.onColDragged,
      onColFreezed: this.onColFreezed,
      onRowFreezed: this.onRowFreezed,
      onNotesSaved: this.onNotesSaved,
      onItemUpdated: this.onItemUpdated,
      onCellEditEnded: this.onCellEditEnded,
      onGroupPanelChanged: this.onGroupPanelChanged,
      onColumnsMappingChange: this.onColumnsMappingChange,
      onCurrentItemChanged: this.onCurrentItemChanged,
      onClearSelection: this.onClearSelection,
      gridRef: this.state.gridRef,
      grid: this.state.gridRef && this.state.gridRef.grid,
      userViewState: this.state.userViewState,
      refetching: this.props.refetching,
      refetch: this.props.refetch,
      hasAllColumnsHidden: this.state.hasAllColumnsHidden,
      frozenColumns: this.state.frozenColumns,
      isLoading: this.props.loading,
      isRefetching: this.props.refetching,
      isCollapseLoading: this.state.isCollapseLoading,
      setCollapseLoading: this.setCollapseLoading,
      hasDataToDisplay: this.state.hasDataToDisplay,
      clearCollectionAndResetChangedOrderItems: this.clearCollectionAndResetChangedOrderItems,
      playResetAnimation: this.state.playResetAnimation,
      handleResetAnimation: this.handleResetAnimation,
      setFlexGridFilter: this.setFlexGridFilter,
      getGridFilterDefinition: this.getGridFilterDefinition,
      clearInternalFilters: this.clearInternalFilters,
      toggleMultiSort: this.toggleMultiSort,
      multiColumnSort: this.state.multiColumnSort,
      updatePriceFields: this.updatePriceFields
    });
  }
}

// $FlowFixMe
export default withApollo(GridApiController);
